import { resolveUserTimezone } from "./timezone";

// === CUSTOMIZE THESE ===
const LOCALE = "en-US";
const CURRENCY = "USD";

let displayTimezone = resolveUserTimezone(undefined);
let timezoneConfigured = false;

export function configureDisplayTimezone(zone: string): void {
  const resolvedZone = resolveUserTimezone(zone);
  if (timezoneConfigured && resolvedZone === displayTimezone) return;
  displayTimezone = resolvedZone;
  timezoneConfigured = true;
  console.log("[Frontend][Timezone] Display timezone configured", { timezone: resolvedZone });
}

export function getDisplayTimezone(): string {
  return displayTimezone;
}

// Intl-based formatters shared across all dashboards and UI (no string concat for currency/dates).

/** Format a number with locale-aware grouping (e.g. 1,234,567.89). */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat(LOCALE).format(n);
}

export function formatMacroValue(
  value: number | null | undefined,
  unit = "g",
  precision: 0 | 1 = 1,
): string {
  if (value === null || value === undefined) return "—";
  const rounded = precision === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${formatNumber(rounded)}${unit}`;
}

/** Format a number as a locale-aware currency string (e.g. $99.95). */
export function formatCurrency(n: number): string {
  return new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: CURRENCY,
  }).format(n);
}

/** Format epoch-ms as "medium" date + short time (e.g. Jun 22, 2026, 12:00 PM). */
export function formatDateTime(ms: number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: displayTimezone,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ms));
}

/** Format epoch-ms as "medium" date only (e.g. Jun 22, 2026). */
export function formatDate(ms: number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: displayTimezone,
    dateStyle: "medium",
  }).format(new Date(ms));
}

/** Format epoch-ms as short time only (e.g. 12:00 PM). */
export function formatTime(ms: number): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: displayTimezone,
    timeStyle: "short",
  }).format(new Date(ms));
}

/** Format epoch-ms as a relative phrase via Intl.RelativeTimeFormat (e.g. "2 hours ago"). */
export function formatRelative(ms: number): string {
  const diffSec = Math.round((ms - Date.now()) / 1000);
  const absSec = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });
  if (absSec < 60) return rtf.format(Math.round(diffSec), "second");
  if (absSec < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (absSec < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  return rtf.format(Math.round(diffSec / 86400), "day");
}
